import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { BADGE_CATEGORIES, getBadgeIcon, getBadgeLabel, type BadgeIconName, type BadgeCategoryGroup } from './badge-catalog';

interface Props {
  value: string | null;
  onChange: (name: BadgeIconName | null) => void;
  themeColor?: string | null;
  size?: 'sm' | 'md';
}

const TINT: Record<string, { bg: string; ring: string; text: string }> = {
  blue:    { bg: 'bg-blue-500',    ring: 'ring-blue-500/40',    text: 'text-blue-500' },
  emerald: { bg: 'bg-emerald-500', ring: 'ring-emerald-500/40', text: 'text-emerald-500' },
  purple:  { bg: 'bg-purple-500',  ring: 'ring-purple-500/40',  text: 'text-purple-500' },
  orange:  { bg: 'bg-orange-500',  ring: 'ring-orange-500/40',  text: 'text-orange-500' },
  pink:    { bg: 'bg-pink-500',    ring: 'ring-pink-500/40',    text: 'text-pink-500' },
  cyan:    { bg: 'bg-cyan-500',    ring: 'ring-cyan-500/40',    text: 'text-cyan-500' },
  amber:   { bg: 'bg-amber-500',   ring: 'ring-amber-500/40',   text: 'text-amber-500' },
  indigo:  { bg: 'bg-indigo-500',  ring: 'ring-indigo-500/40',  text: 'text-indigo-500' },
  red:     { bg: 'bg-red-500',     ring: 'ring-red-500/40',     text: 'text-red-500' },
  teal:    { bg: 'bg-teal-500',    ring: 'ring-teal-500/40',    text: 'text-teal-500' },
};

function getTint(theme?: string | null) { return TINT[theme || 'indigo'] || TINT.indigo!; }

export default function BadgePicker({ value, onChange, themeColor, size = 'md' }: Props) {
  const [activeGroup, setActiveGroup] = useState<BadgeCategoryGroup['id']>(BADGE_CATEGORIES[0]!.id);
  const tint = getTint(themeColor);
  const current = BADGE_CATEGORIES.find(g => g.id === activeGroup) ?? BADGE_CATEGORIES[0]!;
  const dim = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const iconSize = size === 'sm' ? 14 : 16;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1">
          {BADGE_CATEGORIES.map(g => (
            <button
              key={g.id}
              type="button"
              onClick={() => setActiveGroup(g.id)}
              className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-md border transition ${
                activeGroup === g.id
                  ? `${tint.bg} text-white border-transparent`
                  : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Rimuovi badge"
          >
            <X size={10} /> Nessuno
          </button>
        )}
      </div>

      <div className="grid grid-cols-8 gap-1.5 p-2 rounded-lg bg-muted/40 border border-border">
        {current.icons.map(entry => {
          const Icon = entry.icon;
          const active = value === entry.name;
          return (
            <button
              key={entry.name}
              type="button"
              onClick={() => onChange(entry.name)}
              title={entry.label}
              aria-label={entry.label}
              aria-pressed={active}
              className={`${dim} relative rounded-md flex items-center justify-center border transition ${
                active
                  ? `${tint.bg} text-white border-transparent ring-2 ${tint.ring}`
                  : `bg-card border-border ${tint.text} hover:bg-muted`
              }`}
            >
              <Icon size={iconSize} />
              {active && <Check size={9} className="absolute -top-1 -right-1 bg-foreground text-background rounded-full p-[1px]" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Rendering inline del badge selezionato (cerchio colorato + icona). */
export function WorkgroupBadge({
  name,
  themeColor,
  size = 28,
}: { name: string | null; themeColor?: string | null; size?: number }) {
  const Icon = getBadgeIcon(name);
  if (!Icon) return null;
  const tint = getTint(themeColor);
  const label = getBadgeLabel(name) ?? '';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full ${tint.bg} text-white shadow-sm`}
      style={{ width: size, height: size }}
      title={label}
      aria-label={label}
    >
      <Icon size={Math.round(size * 0.55)} />
    </span>
  );
}
